import React, { useState, useEffect } from 'react';
import { purchaseOrdersAPI, jobsAPI, contractorsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DatePicker } from '../components/DatePicker';
import { Plus, Search, Edit, Send, ShoppingCart, Trash2, Download, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { PDFViewer } from '../components/ui/pdf-viewer';

const statusColors = {
  draft: 'status-draft',
  sent: 'status-sent',
  acknowledged: 'status-in_progress',
  completed: 'status-completed',
  cancelled: 'status-cancelled',
};

export const PurchaseOrdersPage = () => {
  const [pos, setPos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [formData, setFormData] = useState({
    job_id: '',
    contractor_id: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    delivery_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [posRes, jobsRes, contractorsRes] = await Promise.all([
        purchaseOrdersAPI.getAll(),
        jobsAPI.getAll(),
        contractorsAPI.getAll()
      ]);
      setPos(posRes.data);
      setJobs(jobsRes.data);
      setContractors(contractorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await purchaseOrdersAPI.create(formData);
      toast.success('Purchase Order created');
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleStatusChange = async (poId, newStatus) => {
    try {
      await purchaseOrdersAPI.update(poId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setFormData({
      job_id: '',
      contractor_id: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: '',
      delivery_date: '',
    });
  };

  const handleDownloadPdf = async (poId, poNumber) => {
    try {
      const response = await purchaseOrdersAPI.downloadPdf(poId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO-${poNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewPdf = async (poId, poNumber) => {
    try {
      const response = await purchaseOrdersAPI.downloadPdf(poId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(`Purchase Order ${poNumber}`);
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error('Failed to load PDF');
    }
  };

  const getJobTitle = (jobId) => jobs.find(j => j.id === jobId)?.title || 'Unknown';
  const getContractorName = (contractorId) => contractors.find(c => c.id === contractorId)?.name || 'Unknown';

  const filteredPos = pos.filter(p =>
    p.po_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Purchase Orders</h1>
          <p className="text-muted-foreground mt-1">Manage orders to contractors</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="uppercase font-bold tracking-wide rounded-sm" data-testid="add-po-btn">
              <Plus className="h-4 w-4 mr-2" />
              New PO
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Purchase Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Select Job *</Label>
                  <Select value={formData.job_id} onValueChange={(v) => setFormData({ ...formData, job_id: v })}>
                    <SelectTrigger className="bg-input border-border rounded-sm" data-testid="po-job">
                      <SelectValue placeholder="Select a job" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {jobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Contractor *</Label>
                  <Select value={formData.contractor_id} onValueChange={(v) => setFormData({ ...formData, contractor_id: v })}>
                    <SelectTrigger className="bg-input border-border rounded-sm" data-testid="po-contractor">
                      <SelectValue placeholder="Select a contractor" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {contractors.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name} - {c.trade_type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Delivery Date</Label>
                <DatePicker
                  value={formData.delivery_date}
                  onChange={(d) => setFormData({ ...formData, delivery_date: d })}
                  placeholder="Pick delivery date"
                  data-testid="po-delivery-date"
                />
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-sm">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2 border border-border rounded-sm p-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="col-span-6 bg-input border-border rounded-sm text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="col-span-2 bg-input border-border rounded-sm text-sm"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        className="col-span-3 bg-input border-border rounded-sm text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="col-span-1"
                        disabled={formData.items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t border-border">
                    <p className="font-mono font-bold">
                      Subtotal: ${calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input border-border rounded-sm"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="uppercase font-bold tracking-wide rounded-sm" data-testid="po-submit">
                  Create PO
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search purchase orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-sm"
          data-testid="po-search"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredPos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mb-4" />
              <p>No purchase orders found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>PO #</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Contractor</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Delivery</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPos.map((po) => (
                  <TableRow key={po.id} className="border-border" data-testid={`po-row-${po.id}`}>
                    <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
                    <TableCell>{getJobTitle(po.job_id)}</TableCell>
                    <TableCell>{getContractorName(po.contractor_id)}</TableCell>
                    <TableCell className="font-mono">${po.total?.toFixed(2)}</TableCell>
                    <TableCell>
                      {po.delivery_date ? format(new Date(po.delivery_date), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Select value={po.status} onValueChange={(v) => handleStatusChange(po.id, v)}>
                        <SelectTrigger className={`w-auto h-auto px-2 py-1 border ${statusColors[po.status]} rounded-sm text-xs font-mono uppercase`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="acknowledged">Acknowledged</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" title="View PDF" onClick={() => handleViewPdf(po.id, po.po_number)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download PDF" onClick={() => handleDownloadPdf(po.id, po.po_number)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Send PO">
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Modal */}
      <PDFViewer
        isOpen={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={pdfUrl}
        title={pdfTitle}
      />
    </div>
  );
};

export default PurchaseOrdersPage;
